<script setup>
import UserController from '@controllers/User/UserController'
import { router, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const page = usePage()

const props = defineProps({
  hidden: {
    type: Boolean,
    default: false,
  },
})

const isClosing = ref(false)
const isVisible = ref(!props.hidden)

const currentUserId = computed(() => page.props.auth?.user?.id)

function closeCard() {
  isClosing.value = true

  router.put(
    UserController.update(currentUserId.value).url,
    {
      hide_dashboard_welcome_message: true,
    },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        isVisible.value = false
      },
      onFinish: () => {
        isClosing.value = false
      },
    },
  )
}
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    :draggable="false"
    :style="{ width: '90vw', maxWidth: '42rem' }"
    :pt="{
      mask: {
        class: 'backdrop-blur-[2px] !bg-surface-900/60',
      },
    }">
    <template #header>
      <div class="flex w-full flex-row flex-wrap items-center justify-between gap-4 px-8 pt-4">
        <span class="text-surface-900 text-2xl font-semibold">{{ t('title') }}</span>
        <span
          class="bg-success-50 text-success-800 border-success-200 inline-flex w-fit items-center rounded-md border px-2.5 py-1 text-xs font-medium">
          {{ t('badge') }}
        </span>
      </div>
    </template>

    <div class="text-surface-700 space-y-4 px-8 py-5 leading-relaxed">
      <i18n-t keypath="paragraphs.intro" tag="p">
        <template #beta>
          <strong>{{ t('paragraphs.introBeta') }}</strong>
        </template>
      </i18n-t>
      <i18n-t keypath="paragraphs.valueProposition" tag="p">
        <template #highlight>
          <strong>{{ t('paragraphs.valuePropositionHighlight') }}</strong>
        </template>
      </i18n-t>
      <i18n-t keypath="paragraphs.development" tag="p">
        <template #highlight>
          <strong>{{ t('paragraphs.developmentHighlight') }}</strong>
        </template>
      </i18n-t>
      <div class="border-surface-200 mt-6 space-y-1 border-t pt-5">
        <p class="text-surface-900 font-medium">{{ t('paragraphs.closing') }}</p>
        <p class="text-brand-green-1 text-lg font-semibold">{{ t('signature') }}</p>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button :label="t('actions.hidePermanently')" :loading="isClosing" @click="closeCard" />
      </div>
    </template>
  </Dialog>
</template>

<i18n>
{
  "de": {
    "title": "Willkommen bei FinEstHub",
    "badge": "Offene Beta",
    "paragraphs": {
      "intro": "Wir freuen uns sehr, dir ab sofort die {beta} unserer Plattform zur Verfügung zu stellen.",
      "introBeta": "offene Beta-Version",

      "valueProposition": "FinEstHub bringt Immobilieninvestment-Finanzierungen und passende Finanzierungspartner zusammen – {highlight}. Du kannst schon jetzt über die Finanzierungsanfrage dein Immobilienprojekt einreichen und mit passenden Bankpartnern zusammengeführt werden, die wirklich zu deinem Vorhaben passen.",
      "valuePropositionHighlight": "strukturiert, transparent und ohne unnötige Umwege",

      "development": "Die Plattform entwickelt sich stetig weiter und wir arbeiten mit Hochdruck an neuen Funktionen, die dir die Nutzung noch einfacher machen. {highlight} Jede Rückmeldung hilft, FinEstHub genau dort zu verbessern, wo es für dich zählt.",
      "developmentHighlight": "Deine Erfahrungen sind dabei besonders wertvoll für uns:",

      "closing": "Wir wünschen dir einen guten Start!"
    },
    "signature": "Dein FinEstHub-Team",
    "actions": {
      "hidePermanently": "Okay, auf geht's"
    }
  },
  "en": {
    "title": "Welcome to FinEstHub",
    "badge": "Open Beta",
    "paragraphs": {
      "intro": "We're delighted to give you access to the {beta} of our platform, effective immediately.",
      "introBeta": "open beta version",

      "valueProposition": "FinEstHub brings real estate investment financing and the right financing partners together – {highlight}. You can already submit your real estate project through the financing request and be matched with banking partners that truly fit your plans.",
      "valuePropositionHighlight": "structured, transparent and without unnecessary detours",

      "development": "The platform is constantly evolving, and we're working hard on new features that make it even easier to use. {highlight} Every piece of feedback helps us improve FinEstHub exactly where it matters to you.",
      "developmentHighlight": "Your experiences are especially valuable to us:",

      "closing": "We wish you a great start!"
    },
    "signature": "Your FinEstHub team",
    "actions": {
      "hidePermanently": "Okay, let's get started"
    }
  }
}
</i18n>
